/**
 * Escrow Email Service
 * Sends transactional emails when escrow status changes via the send-escrow-emails Edge Function.
 */

import { supabase } from '../lib/supabase';

const ESCROW_EMAIL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-escrow-emails`;

const getAuthHeaders = async () => {
  try {
    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token;
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    return headers;
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

const callEscrowEmailFunction = async (type, data) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(ESCROW_EMAIL_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, data }),
    });
    if (!response?.ok) {
      const err = await response?.json()?.catch(() => ({}));
      console.warn(`[EscrowEmail] Failed "${type}":`, err?.error || response?.statusText);
      return { success: false, error: err?.error };
    }
    return { success: true };
  } catch (err) {
    console.warn(`[EscrowEmail] Error "${type}":`, err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Fetch user email and name from Supabase for a given user_id.
 */
const getUserInfo = async (userId) => {
  if (!userId) return null;
  try {
    const { data } = await supabase
      ?.from('user_profiles')
      ?.select('full_name, email')
      ?.eq('id', userId)
      ?.maybeSingle();
    return data;
  } catch {
    return null;
  }
};

/**
 * Send escrow released email.
 */
export const sendEscrowReleasedEmail = async ({ userId, userEmail, userName, transactionRef, propertyTitle, amount, currency, transactionId }) => {
  let email = userEmail;
  let name = userName;
  if (!email && userId) {
    const info = await getUserInfo(userId);
    email = info?.email;
    name = info?.full_name || name;
  }
  if (!email) return { success: false, skipped: true };
  return callEscrowEmailFunction('escrow_released', {
    user_email: email,
    user_name: name,
    transaction_ref: transactionRef,
    property_title: propertyTitle,
    amount,
    currency,
    transaction_id: transactionId,
  });
};

/**
 * Send escrow disputed email.
 */
export const sendEscrowDisputedEmail = async ({ userId, userEmail, userName, transactionRef, propertyTitle, amount, currency, disputeReason }) => {
  let email = userEmail;
  let name = userName;
  if (!email && userId) {
    const info = await getUserInfo(userId);
    email = info?.email;
    name = info?.full_name || name;
  }
  if (!email) return { success: false, skipped: true };
  return callEscrowEmailFunction('escrow_disputed', {
    user_email: email,
    user_name: name,
    transaction_ref: transactionRef,
    property_title: propertyTitle,
    amount,
    currency,
    dispute_reason: disputeReason,
  });
};

/**
 * Send escrow refunded email.
 */
export const sendEscrowRefundedEmail = async ({ userId, userEmail, userName, transactionRef, propertyTitle, amount, currency }) => {
  let email = userEmail;
  let name = userName;
  if (!email && userId) {
    const info = await getUserInfo(userId);
    email = info?.email;
    name = info?.full_name || name;
  }
  if (!email) return { success: false, skipped: true };
  return callEscrowEmailFunction('escrow_refunded', {
    user_email: email,
    user_name: name,
    transaction_ref: transactionRef,
    property_title: propertyTitle,
    amount,
    currency,
  });
};

/**
 * Send escrow resolved email.
 */
export const sendEscrowResolvedEmail = async ({ userId, userEmail, userName, transactionRef, propertyTitle, amount, currency, resolutionNote }) => {
  let email = userEmail;
  let name = userName;
  if (!email && userId) {
    const info = await getUserInfo(userId);
    email = info?.email;
    name = info?.full_name || name;
  }
  if (!email) return { success: false, skipped: true };
  return callEscrowEmailFunction('escrow_resolved', {
    user_email: email,
    user_name: name,
    transaction_ref: transactionRef,
    property_title: propertyTitle,
    amount,
    currency,
    resolution_note: resolutionNote,
  });
};

/**
 * Send commission released email to agent or notary.
 */
export const sendCommissionReleasedEmail = async ({ recipientEmail, recipientName, transactionRef, propertyTitle, commissionAmount, commissionRate, currency, role }) => {
  if (!recipientEmail) return { success: false, skipped: true };
  return callEscrowEmailFunction('commission_released', {
    recipient_email: recipientEmail,
    recipient_name: recipientName,
    transaction_ref: transactionRef,
    property_title: propertyTitle,
    commission_amount: commissionAmount,
    commission_rate: commissionRate,
    currency,
    role,
  });
};

/**
 * Send escrow transaction receipt email (PDF-style) with full financial breakdown.
 */
export const sendEscrowReceiptEmail = async ({
  recipientId, recipientEmail, recipientName, recipientRole,
  transactionRef, propertyTitle,
  grossAmount, taxWithholdingAmount, fxFeeAmount, netAmount,
  fxRateApplied, payoutCurrency, taxRateApplied,
  payoutChannel, approvedAt, paidAt, settlementDate,
  receiptNumber,
}) => {
  let email = recipientEmail;
  let name = recipientName;
  if (!email && recipientId) {
    const info = await getUserInfo(recipientId);
    email = info?.email;
    name = info?.full_name || name;
  }
  if (!email) return { success: false, skipped: true };
  return callEscrowEmailFunction('escrow_receipt', {
    recipient_email: email,
    recipient_name: name,
    recipient_role: recipientRole,
    transaction_ref: transactionRef,
    property_title: propertyTitle,
    gross_amount: grossAmount,
    tax_withholding_amount: taxWithholdingAmount,
    fx_fee_amount: fxFeeAmount,
    net_amount: netAmount,
    fx_rate_applied: fxRateApplied,
    payout_currency: payoutCurrency,
    tax_rate_applied: taxRateApplied,
    payout_channel: payoutChannel,
    approved_at: approvedAt,
    paid_at: paidAt,
    settlement_date: settlementDate,
    receipt_number: receiptNumber,
  });
};

/**
 * Auto-send receipt when a payout transitions to 'paid' status.
 * Call this after updating commission_payouts.status = 'paid'.
 */
export const autoSendPayoutReceipt = async (payout) => {
  if (!payout) return { success: false, skipped: true };
  const receiptNumber = `KS-${payout?.id?.slice(0, 8)?.toUpperCase()}-${new Date(payout.created_at || Date.now())?.getFullYear()}`;
  return sendEscrowReceiptEmail({
    recipientId: payout?.recipient_id,
    recipientEmail: payout?.recipient_email,
    recipientName: payout?.recipient_name,
    recipientRole: payout?.recipient_role || 'agent',
    transactionRef: payout?.transaction_ref,
    propertyTitle: payout?.property_title,
    grossAmount: payout?.amount,
    taxWithholdingAmount: payout?.tax_withholding_amount,
    fxFeeAmount: payout?.fx_fee_amount,
    netAmount: payout?.net_after_tax || payout?.net_payout_amount,
    fxRateApplied: payout?.fx_rate_applied,
    payoutCurrency: payout?.payout_currency || 'XOF',
    taxRateApplied: payout?.tax_rate_applied,
    payoutChannel: payout?.payout_channel,
    approvedAt: payout?.approved_at,
    paidAt: payout?.paid_at,
    settlementDate: payout?.paid_at,
    receiptNumber,
  });
};

export default {
  sendEscrowReleasedEmail,
  sendEscrowDisputedEmail,
  sendEscrowRefundedEmail,
  sendEscrowResolvedEmail,
  sendCommissionReleasedEmail,
  sendEscrowReceiptEmail,
  autoSendPayoutReceipt,
};
